import { useCallback, useRef, useState } from 'react';

import { filesExtensions } from '@app/components/DraggerComponent/data/constants';
import type { NDraggerComponent } from '@app/components/DraggerComponent/DraggerComponent.types';
import { FRONT_WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NClientFiles } from '@app/types/ClientFiles';

import type { ChangeEvent, DragEvent } from 'react';

export const useDraggerComponent = ({
  id,
  setFilesWithData,
  onLoadStart,
  onLoadEnd,
  isChat,
  extraExtensions = [],
}: NDraggerComponent.UseDraggerComponentProps) => {
  const { showNotification } = useNotification();
  const [drag, setDrag] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const dragStartHandler = useCallback(
    ({ preventDefault }: DragEvent<HTMLDivElement>) => {
      preventDefault();
      setDrag(true);
    },
    []
  );

  const dragLeaveHandler = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDrag(false);
  }, []);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDrag(true);
  }, []);

  const formatFilesHandler = useCallback(
    async (files: File[]) => {
      const filteredFiles = files.filter(({ name }) => {
        const extension = name.split('.').pop()?.toLowerCase();

        return [...filesExtensions, ...extraExtensions].includes(
          extension || ''
        );
      });

      if (filteredFiles.length === 0) {
        return showNotification(
          'Данный файл не может быть отправлен. Проверьте формат загружаемого файла.',
          FRONT_WARNING
        );
      }

      if (!id && isChat) {
        showNotification(
          'Не удалось отправить файлы. Попробуйте перезагрузить страницу.',
          FRONT_WARNING
        );

        return;
      }

      const newFiles: NClientFiles.ModelClientFileForCreation[] = [];

      setIsFileLoading(true);
      onLoadStart?.();

      for (const file of filteredFiles) {
        try {
          const base64String = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
              if (typeof reader.result === 'string') {
                resolve(reader.result.replace(/^data:.+;base64,/, ''));
              } else {
                reject(new Error('Не удалось загрузить файл'));
              }
            };

            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
          });

          newFiles.push({
            ClientId: id ?? 0,
            Description: '',
            FileName: file.name,
            Data: base64String,
            Size: file.size,
          });
        } catch (error) {
          showNotification(error, FRONT_WARNING);
        }
      }

      setIsFileLoading(false);

      onLoadEnd?.();

      setFilesWithData((prev) => [...prev, ...newFiles]);
    },
    [
      id,
      setFilesWithData,
      showNotification,
      onLoadStart,
      onLoadEnd,
      isChat,
      extraExtensions,
    ]
  );

  const onDropHandler = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (isFileLoading) return;
      const files = [...Array.from(event.dataTransfer.files)];

      formatFilesHandler(files);
      setDrag(false);
    },
    [formatFilesHandler, isFileLoading]
  );

  const deleteFileFromTheList = useCallback(
    (fileName: string | null, e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      setFilesWithData((prev: NClientFiles.ModelClientFileForCreation[]) => [
        ...prev.filter((file) => file.FileName !== fileName),
      ]);
    },
    [setFilesWithData]
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, fileName: string | null) => {
      if (fileName) {
        setFilesWithData((prev) => [
          ...prev.map((item) => {
            if (item.FileName === fileName) {
              return {
                ...item,
                Description: e.target.value,
              };
            } else {
              return item;
            }
          }),
        ]);
      }
    },
    [setFilesWithData]
  );

  const uploadFileManually = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();

      const files = event.target.files ? Array.from(event.target.files) : [];

      formatFilesHandler(files);
      event.target.value = '';

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [formatFilesHandler]
  );

  const handleFileNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, fileName: string | null) => {
      if (fileName) {
        setFilesWithData((prev) => [
          ...prev.map((item) => {
            if (item.FileName === fileName) {
              return {
                ...item,
                NewName: `${e.target.value}.${fileName.split('.').pop()}`,
              };
            } else {
              return item;
            }
          }),
        ]);
      }
    },
    [setFilesWithData]
  );

  return {
    drag,
    dragStartHandler,
    dragLeaveHandler,
    onDropHandler,
    deleteFileFromTheList,
    handleDescriptionChange,
    uploadFileManually,
    handleFileNameChange,
    fileInputRef,
    handleDragEnter,
    isFileLoading,
  };
};
